import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { useCreateCommitment, useMe } from '../api/queries';
import { Button, Field, Icon, Modal, TextArea, useNotices } from '../ui';
import type { Candidate } from '../ui/OwnerModal';
import { initialsOf } from '../ui/initials';

/**
 * An action somebody writes down themselves, on the meeting's page.
 *
 * Vladimir, 2026-09-23: *"add possibility to create actions in the meeting
 * page so that I can add something myself, assign owner, deadline etc and
 * then post to slack with my created as well"*. It is an ordinary action the
 * moment it exists — Slack, a DM, Asana and the deadline all work on it — and
 * it is marked as written by a person, so nobody takes it for the model's.
 *
 * The owner is a choice among the people who were there, you, or an address;
 * leaving it empty is allowed, as it is for anything promised in a meeting.
 */
export function NewAction({
  meetingId,
  candidates,
  onClose,
}: {
  meetingId: string;
  candidates: Candidate[];
  onClose: () => void;
}): ReactElement {
  const create = useCreateCommitment(meetingId);
  const { show } = useNotices();
  const [text, setText] = useState('');
  const [owner, setOwner] = useState<Candidate | null>(null);
  const [other, setOther] = useState('');
  const [due, setDue] = useState('');

  const address = owner?.email ?? (other.includes('@') ? other.trim() : '');
  const save = (e: FormEvent) => {
    e.preventDefault();
    if (text.trim().length < 3) return;
    create.mutate(
      {
        text: text.trim(),
        ...(address ? { ownerEmail: address } : {}),
        ...(owner?.name ? { ownerName: owner.name } : {}),
        // Nine in the morning, local — the same moment a deadline set from
        // the ledger lands on, so "due today" means the same everywhere.
        ...(due ? { dueAt: new Date(`${due}T09:00:00`).toISOString() } : {}),
      },
      {
        onSuccess: () => {
          onClose();
          show({ tone: 'success', text: 'Added — it is in the meeting’s actions now.' });
        },
        onError: (err) => show({ tone: 'failure', text: err.message }),
      },
    );
  };

  return (
    <Modal title="Add an action" onClose={onClose}>
      <form className="stack" style={{ gap: 14 }} onSubmit={save}>
        <TextArea
          label="What"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Send Harbor the revised survey dates"
          rows={2}
          autoFocus
          maxLength={500}
        />
        <div className="stack" style={{ gap: 6 }}>
          <span className="field-label">Who</span>
          <div className="cluster">
            {candidates.map((c) => (
              <button
                key={c.email}
                type="button"
                aria-pressed={owner?.email === c.email}
                className={`who ${c.me ? 'me' : ''} ${owner?.email === c.email ? 'on' : ''}`}
                onClick={() => {
                  setOwner(owner?.email === c.email ? null : c);
                  setOther('');
                }}
              >
                <span className="avatar">{initialsOf(c.name, c.email)}</span>
                {c.me ? 'Me' : (c.name ?? c.email)}
              </button>
            ))}
          </div>
          <Field
            placeholder="Someone else — their address…"
            type="email"
            value={other}
            onChange={(e) => {
              setOther(e.target.value);
              setOwner(null);
            }}
          />
          {!address ? <span className="why">nobody yet — an owner can be given later</span> : null}
        </div>
        <label className="stack" style={{ gap: 6 }}>
          <span className="field-label">By when</span>
          <input
            className="field"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
            aria-label="By when"
          />
        </label>
        <div className="cluster">
          <Button
            type="submit"
            variant="primary"
            disabled={text.trim().length < 3}
            working={create.isPending ? 'Adding…' : false}
          >
            Add the action
          </Button>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The way in to writing one down — as a button, or as a row of "What it
 * produced".
 *
 * ⚠ Where somebody looks, not only where it belongs. It first lived only in
 * the header of "What was promised", small and borderless, below the fold —
 * Vladimir, the same evening: *"i dont see the button to create new action in
 * meeting"*. It is in the card at the top now too, and in every state of the
 * section, not only the one where nothing was promised.
 */
export function AddAction({
  meetingId,
  attendees,
  names,
  as = 'button',
}: {
  meetingId: string;
  attendees: string[];
  names: Map<string, string>;
  as?: 'button' | 'row';
}): ReactElement {
  const [open, setOpen] = useState(false);
  const me = useMe();
  // Who an action can be given to: you, and the people who were there.
  const candidates: Candidate[] = [
    ...(me.data ? [{ email: me.data.email, name: me.data.name, me: true }] : []),
    ...attendees
      .filter((a) => a.includes('@') && a.toLowerCase() !== me.data?.email.toLowerCase())
      .map((a) => ({ email: a, name: names.get(a.toLowerCase()) ?? null })),
  ];
  return (
    <>
      {as === 'row' ? (
        <button type="button" className="count-row" onClick={() => setOpen(true)}>
          <span className="mono" style={{ width: 20 }}>
            +
          </span>
          <span className="grow">Add an action</span>
          <Icon name="chevron" size={15} className="t-secondary" />
        </button>
      ) : (
        <Button size="sm" onClick={() => setOpen(true)}>
          + Add an action
        </Button>
      )}
      {open ? (
        <NewAction meetingId={meetingId} candidates={candidates} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}
